import type {
  ParentTicketAttachment,
  ParentTicketAttachmentRow,
  ParentTicketAttachmentSource,
  ParentTicketAttachmentType,
} from '@/types/ticket'

export function mapApiAttachments(raw: unknown): ParentTicketAttachment[] {
  if (!Array.isArray(raw)) return []

  return raw.flatMap((item) => {
      const row = item as Record<string, unknown>
      const fileName = String(row.fileName ?? row.file_name ?? '')
      if (!fileName) return []

      const attachmentType = (row.attachmentType ?? row.attachment_type ?? 'work_permit') as ParentTicketAttachmentType
      const source = (row.source ?? 'form') as ParentTicketAttachmentSource

      const attachment: ParentTicketAttachment = {
        ...(row.id != null ? { id: Number(row.id) } : {}),
        attachmentType: attachmentType === 'other' ? 'other' : 'work_permit',
        source: source === 'task' ? 'task' : 'form',
        fileName,
        filePath: (row.filePath ?? row.file_path) as string | undefined,
        fileSize:
          row.fileSize != null
            ? Number(row.fileSize)
            : row.file_size != null
              ? Number(row.file_size)
              : null,
        mimeType:
          (row.mimeType ?? row.mime_type) != null
            ? String(row.mimeType ?? row.mime_type)
            : null,
        childTicketId:
          row.childTicketId != null
            ? Number(row.childTicketId)
            : row.child_ticket_id != null
              ? Number(row.child_ticket_id)
              : null,
      }

      return [attachment]
    })
}

export function formAttachmentRowsFromTicket(
  attachments: ParentTicketAttachment[] | undefined,
): ParentTicketAttachmentRow[] {
  return (attachments ?? [])
    .filter((item) => item.source === 'form')
    .map((item) => ({
      id: item.id,
      attachmentType: item.attachmentType,
      fileName: item.fileName,
      filePath: item.filePath,
    }))
}

export function buildAttachmentUploadFormData(options: {
  rows: ParentTicketAttachmentRow[]
  source: ParentTicketAttachmentSource
  replace?: boolean
  childTicketId?: number | null
}): FormData {
  const fd = new FormData()
  const keepIds: number[] = []
  let fileIndex = 0

  options.rows.forEach((row) => {
    if (row.file) {
      fd.append('files[]', row.file)
      fd.append(`attachment_types[${fileIndex}]`, row.attachmentType)
      fileIndex += 1
    } else if (row.id != null) {
      keepIds.push(row.id)
    }
  })

  keepIds.forEach((id) => fd.append('keep_attachment_ids[]', String(id)))
  fd.append('source', options.source)
  if (options.replace) fd.append('replace', '1')
  if (options.childTicketId != null) {
    fd.append('child_ticket_id', String(options.childTicketId))
  }

  return fd
}

export function attachmentTypeLabel(type: ParentTicketAttachmentType): string {
  return type === 'work_permit' ? 'Work permit' : 'Other document'
}

export function formatFileSize(bytes?: number | null): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10_240 ? 1 : 0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10_485_760 ? 1 : 0)} MB`
}

export type AttachmentFileKind = 'image' | 'pdf' | 'document' | 'other'

export function attachmentFileKind(fileName: string, mimeType?: string | null): AttachmentFileKind {
  const name = fileName.toLowerCase()
  const mime = (mimeType ?? '').toLowerCase()

  if (mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(name)) return 'image'
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf'
  if (
    mime.includes('word') ||
    mime.includes('document') ||
    name.endsWith('.doc') ||
    name.endsWith('.docx')
  ) {
    return 'document'
  }

  return 'other'
}

export function attachmentFileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.')
  if (dot <= 0 || dot === fileName.length - 1) return 'FILE'
  return fileName.slice(dot + 1).toUpperCase()
}

export function attachmentThumbnailLabel(kind: AttachmentFileKind, fileName: string): string {
  if (kind === 'pdf') return 'PDF'
  return attachmentFileExtension(fileName)
}

export function attachmentThumbnailColors(kind: AttachmentFileKind): {
  bg: string
  color: string
  border: string
} {
  switch (kind) {
    case 'image':
      return { bg: 'blue.50', color: 'blue.600', border: 'blue.100' }
    case 'pdf':
      return { bg: 'red.50', color: 'red.600', border: 'red.100' }
    case 'document':
      return { bg: 'indigo.50', color: 'indigo.700', border: 'indigo.100' }
    default:
      return { bg: 'gray.50', color: 'gray.600', border: 'gray.200' }
  }
}
